/**
 * PATH: apps/quote/src/components/builder/panels/inspector/MarkupOverrideRow.tsx
 * FILE_ID: QB-INSPECTOR-OVERRIDES-MARKUP-ROW-001
 * PURPOSE: Single editable row inside the Manual Overrides tab for one
 *          discipline override (markup / wastage / downtime).
 * WHY: Inlining this in OverridesTab.tsx would push the parent over the
 *      300-line file-size limit (rule 1.7) and obscure the behavioural
 *      contract — controlled input + blur-to-commit + Escape-to-revert
 *      + Clear button + "default: NN%" hint — that all five rows share.
 * FLOW:
 *   ┌──────────────┐    ┌─────────────────┐    ┌──────────────────────┐
 *   │ user types % │ →  │ controlled text │ →  │ blur/Enter           │
 *   │ e.g. "30"    │    │ state in row    │    │  → onCommit(0.30)    │
 *   │              │    │                 │    │ Esc → revert         │
 *   │              │    │                 │    │ Clear → onCommit(null)│
 *   └──────────────┘    └─────────────────┘    └──────────────────────┘
 * DEPENDENCIES:
 *   - overrides-helpers: parsePercentInput, decimalToPercentText, formatDefaultLabel
 * RELATED:
 *   - OverridesTab.tsx — composes 5 of these rows and routes commits
 *                       through the parent inspector's onUpdate channel.
 */

import { useEffect, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import {
  decimalToPercentText,
  formatDefaultLabel,
  parsePercentInput,
} from './overrides-helpers'

export interface MarkupOverrideRowProps {
  /** Stable id used for test selectors and label htmlFor. */
  id: string
  label: string
  /** Current stored value (decimal fraction or null) — e.g. 0.3 for 30%. */
  value: string | number | null | undefined
  /** Engine/config default to show as a placeholder + "default: NN%" hint. */
  configDefault: number
  /**
   * Commit handler. Called with:
   *   - number: a parsed decimal fraction the user typed
   *   - null:   user cleared the field (revert to config default on backend)
   * Never called with an invalid value — the row handles rejection itself.
   */
  onCommit?: (decimalOrNull: number | null) => void
  disabled?: boolean
}

/**
 * Wrapper component for visual consistency across the 5 rows. Pulled out
 * of OverridesTab so the parent stays focused on layout/data flow.
 */
export default function MarkupOverrideRow({
  id,
  label,
  value,
  configDefault,
  onCommit,
  disabled,
}: MarkupOverrideRowProps) {
  // Mirror the persisted value into local text state — that's the only
  // way we can let the user freely edit (including transient invalid
  // states like "3.") without round-tripping to the backend on every keystroke.
  const [text, setText] = useState<string>(decimalToPercentText(value))

  // Re-sync if the line item id changes (selecting a different row) or
  // if the underlying value updates from another channel (formula
  // re-eval / grid edit). Identity check by stringified original value
  // ensures we don't fight the user's own typing.
  useEffect(() => {
    setText(decimalToPercentText(value))
  }, [id, value])

  const isReadOnly = disabled || !onCommit
  const hasValue = value !== null && value !== undefined && value !== ''
  const placeholderPct = decimalToPercentText(configDefault)

  const commit = () => {
    if (!onCommit) return
    const currentText = decimalToPercentText(value)
    if (text === currentText) return
    const parsed = parsePercentInput(text)
    if (parsed === undefined) {
      setText(currentText)
      return
    }
    onCommit(parsed)
  }

  const handleClear = () => {
    if (!onCommit) return
    setText('')
    onCommit(null)
  }

  return (
    <div data-testid={`overrides-markup-row-${id}`}>
      <div className="flex items-center justify-between mb-1">
        <label
          htmlFor={`overrides-${id}-input`}
          className="block text-xs font-medium text-slate-700 dark:text-slate-300"
        >
          {label}
        </label>
        {hasValue && !isReadOnly && (
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 px-1.5 py-0.5 rounded transition-colors"
            title={`Clear ${label.toLowerCase()} — revert to config default`}
            data-testid={`overrides-${id}-clear`}
          >
            <RotateCcw className="w-3 h-3" />
            Clear
          </button>
        )}
      </div>
      <div className="relative">
        <input
          id={`overrides-${id}-input`}
          type="number"
          inputMode="decimal"
          step="any"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              ;(e.target as HTMLInputElement).blur()
            } else if (e.key === 'Escape') {
              setText(decimalToPercentText(value))
              ;(e.target as HTMLInputElement).blur()
            }
          }}
          placeholder={placeholderPct}
          disabled={isReadOnly}
          className="w-full px-2.5 py-1.5 pr-7 border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-sm disabled:opacity-60 tabular-nums"
          data-testid={`overrides-${id}-value`}
        />
        <span
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none"
          aria-hidden="true"
        >
          %
        </span>
      </div>
      <p className="text-xs text-slate-500 mt-1" data-testid={`overrides-${id}-hint`}>
        {hasValue
          ? `Overriding ${formatDefaultLabel(configDefault)}`
          : formatDefaultLabel(configDefault)}
      </p>
    </div>
  )
}
